import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, ArrowRight, Users, Shield, User, KeyRound, Sparkles, Fingerprint } from "lucide-react";
import DemoVideoButton from "../components/DemoVideo";
import { useAuth, apiCall } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginType, setLoginType] = useState("client");
  const [loginMethod, setLoginMethod] = useState("password"); // "password" or "otp"
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [forgotStep, setForgotStep] = useState("email");
  const [emailSent, setEmailSent] = useState(true);
  const [fallbackOtp, setFallbackOtp] = useState("");
  // OTP login state
  const [otpEmail, setOtpEmail] = useState("");
  const [otpLoginStep, setOtpLoginStep] = useState("email"); // "email" or "verify"
  const [loginOtp, setLoginOtp] = useState("");
  const [otpEmailSent, setOtpEmailSent] = useState(true);
  const [otpFallback, setOtpFallback] = useState("");

  const { login, loginWithToken, user } = useAuth();
  const navigate = useNavigate();

  const navAfterLogin = (role) => {
    if (role === "admin") navigate("/admin");
    else if (role === "coach") navigate("/coach");
    else if (role === "expert") navigate("/expert");
    else navigate("/dashboard");
  };

  useEffect(() => {
    if (user) navAfterLogin(user.role);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (user) return null;

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) navAfterLogin(result.user?.role);
  };

  const handleSendLoginOtp = async () => {
    if (!otpEmail) return toast.error("Enter your email");
    setLoading(true);
    const res = await apiCall("POST", "/auth/send-login-otp", { email: otpEmail });
    setLoading(false);
    if (res.success) {
      setOtpEmailSent(res.data.email_sent !== false);
      setOtpFallback(res.data.otp_fallback || "");
      setOtpLoginStep("verify");
      toast.success(res.data.email_sent !== false ? "OTP sent to your email" : "OTP generated");
    } else {
      toast.error(res.error || "Failed to send OTP");
    }
  };

  const handleVerifyLoginOtp = async () => {
    if (!loginOtp) return toast.error("Enter the OTP");
    setLoading(true);
    const res = await apiCall("POST", "/auth/verify-login-otp", { email: otpEmail, otp: loginOtp });
    setLoading(false);
    if (res.success && res.data.token) {
      loginWithToken(res.data.token, res.data.user);
      toast.success("Login successful!");
      navAfterLogin(res.data.user?.role);
    } else {
      toast.error(res.error || "Invalid OTP");
    }
  };

  const handleTabChange = (type) => {
    setLoginType(type);
    setEmail(""); setPassword("");
    setOtpEmail(""); setLoginOtp(""); setOtpLoginStep("email");
  };

  const handleForgotRequest = async () => {
    if (!forgotEmail) return toast.error("Enter your email");
    setLoading(true);
    const res = await apiCall("POST", `/auth/forgot_password?email=${encodeURIComponent(forgotEmail)}`);
    setLoading(false);
    if (res.success) {
      setEmailSent(res.data.email_sent !== false);
      setFallbackOtp(res.data.otp_fallback || "");
      setForgotStep("otp");
      toast.success(res.data.email_sent !== false ? "OTP sent to your email" : "OTP generated — shown below");
    } else {
      toast.error(res.error || "Email not found");
    }
  };

  const handleResetPassword = async () => {
    if (!otpInput || !newPassword) return toast.error("Fill all fields");
    setLoading(true);
    const res = await apiCall("POST", `/auth/reset_password?email=${encodeURIComponent(forgotEmail)}&otp=${otpInput}&new_password=${encodeURIComponent(newPassword)}`);
    setLoading(false);
    if (res.success) {
      toast.success("Password reset successful! Please login.");
      setShowForgot(false); setForgotStep("email"); setOtpInput(""); setNewPassword(""); setForgotEmail("");
    } else {
      toast.error(res.error || "Invalid OTP");
    }
  };

  return (
    <div className="min-h-screen bg-cosmic-void flex items-center justify-center px-6 py-4" data-testid="login-page">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[400px] h-[400px] rounded-full bg-divine-gold/5 blur-[80px]" />
      </div>

      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md z-10">
        <div className="text-center mb-4">
          <Link to="/" className="inline-block">
            <h1 className="font-heading text-3xl text-divine-gold tracking-wider glow-text">SIWON HARMONY</h1>
          </Link>
          <p className="text-chakra-crown/60 mt-1 text-sm tracking-wide">Return to your sacred journey</p>
          <div className="mt-2 flex justify-center">
            <DemoVideoButton placement="login" label="Watch Demo" />
          </div>
        </div>

        {/* Forgot Password Flow */}
        {showForgot ? (
          <div className="sacred-card" data-testid="forgot-password-form">
            <h2 className="font-heading text-xl text-divine-gold mb-4 flex items-center gap-2">
              <KeyRound className="w-5 h-5" /> Reset Password
            </h2>
            {forgotStep === "email" && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="label-sacred">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-divine-gold/40" />
                    <Input value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="your@email.com"
                      className="pl-12 bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown" data-testid="forgot-email-input" />
                  </div>
                </div>
                <Button onClick={handleForgotRequest} disabled={loading} className="w-full vedic-button" data-testid="send-otp-btn">
                  {loading ? "Sending..." : "Send OTP"}
                </Button>
              </div>
            )}
            {forgotStep === "otp" && (
              <div className="space-y-4">
                {emailSent ? (
                  <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-center">
                    <p className="text-emerald-400 text-sm font-heading">OTP Sent to Your Email</p>
                    <p className="text-chakra-crown/50 text-xs mt-1">Check your inbox at <span className="text-divine-gold">{forgotEmail}</span></p>
                  </div>
                ) : (
                  <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-center">
                    <p className="text-emerald-400 text-sm font-heading">OTP Sent to Your Email</p>
                    <p className="text-chakra-crown/50 text-xs mt-1">Check your inbox at <span className="text-divine-gold">{forgotEmail}</span></p>
                    {fallbackOtp && <p className="text-amber-400 text-xs mt-2">If you didn't receive the email, check spam or try again.</p>}
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="label-sacred">Enter OTP</Label>
                  <Input value={otpInput} onChange={(e) => setOtpInput(e.target.value)} placeholder="6-digit OTP"
                    className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown text-center text-xl tracking-[0.2em]"
                    maxLength={6} data-testid="otp-input" />
                </div>
                <div className="space-y-2">
                  <Label className="label-sacred">New Password</Label>
                  <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password"
                    className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown" data-testid="new-password-input" />
                </div>
                <Button onClick={handleResetPassword} disabled={loading} className="w-full vedic-button" data-testid="reset-password-btn">
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </div>
            )}
            <button onClick={() => { setShowForgot(false); setForgotStep("email"); }}
              className="mt-4 text-divine-gold/60 hover:text-divine-gold text-sm w-full text-center" data-testid="back-to-login">
              Back to Login
            </button>
          </div>
        ) : (
          <>
            {/* Role Tabs */}
            <Tabs value={loginType} onValueChange={handleTabChange} className="mb-3">
              <TabsList className="grid grid-cols-4 bg-cosmic-deep/50 border border-divine-gold/10 p-1 rounded-xl">
                <TabsTrigger value="client" className="data-[state=active]:bg-divine-gold data-[state=active]:text-cosmic-void rounded-lg flex items-center gap-2" data-testid="client-tab">
                  <User className="w-4 h-4" />Client
                </TabsTrigger>
                <TabsTrigger value="coach" className="data-[state=active]:bg-divine-gold data-[state=active]:text-cosmic-void rounded-lg flex items-center gap-2" data-testid="coach-tab">
                  <Users className="w-4 h-4" />Coach
                </TabsTrigger>
                <TabsTrigger value="expert" className="data-[state=active]:bg-divine-gold data-[state=active]:text-cosmic-void rounded-lg flex items-center gap-2" data-testid="expert-tab">
                  <Sparkles className="w-4 h-4" />Expert
                </TabsTrigger>
                <TabsTrigger value="admin" className="data-[state=active]:bg-divine-gold data-[state=active]:text-cosmic-void rounded-lg flex items-center gap-2" data-testid="admin-tab">
                  <Shield className="w-4 h-4" />Admin
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Registration prompt - right below role tabs for visibility */}
            {loginType === "client" ? (
              <div className="flex items-center gap-3 mb-3" data-testid="register-prompt">
                <div className="flex-1 h-px bg-divine-gold/15" />
                <p className="text-chakra-crown/60 text-sm shrink-0">
                  New to the journey?{" "}
                  <Link to="/register" className="text-divine-gold hover:text-divine-gold-light font-heading tracking-wide" data-testid="register-link">
                    Begin Registration
                  </Link>
                </p>
                <div className="flex-1 h-px bg-divine-gold/15" />
              </div>
            ) : (
              <p className="text-center text-chakra-crown/40 text-xs mb-3">
                {loginType === "coach" ? "Coach accounts are created by administrators" : 
                 loginType === "expert" ? "Expert accounts are created by administrators" :
                 "Admin access is restricted"}
              </p>
            )}

            <div className="sacred-card" data-testid="login-form">
              {/* Login Method Toggle */}
              <div className="flex gap-2 mb-4">
                <button onClick={() => setLoginMethod("password")}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-heading tracking-wide transition-all ${
                    loginMethod === "password" ? "bg-divine-gold/15 border border-divine-gold/30 text-divine-gold" : "bg-cosmic-light/5 border border-transparent text-chakra-crown/40 hover:text-chakra-crown/60"
                  }`} data-testid="method-password">
                  <Lock className="w-3.5 h-3.5" /> Password
                </button>
                <button onClick={() => setLoginMethod("otp")}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-heading tracking-wide transition-all ${
                    loginMethod === "otp" ? "bg-divine-gold/15 border border-divine-gold/30 text-divine-gold" : "bg-cosmic-light/5 border border-transparent text-chakra-crown/40 hover:text-chakra-crown/60"
                  }`} data-testid="method-otp">
                  <Fingerprint className="w-3.5 h-3.5" /> Email OTP
                </button>
              </div>

              {/* Password Login */}
              {loginMethod === "password" && (
                <form onSubmit={handlePasswordLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="label-sacred" htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-divine-gold/40" />
                      <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com"
                        className="pl-12 bg-neutral-obsidian/50 border border-divine-gold/20 focus:border-divine-gold text-chakra-crown placeholder:text-divine-gold/30"
                        required data-testid="email-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-sacred" htmlFor="password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-divine-gold/40" />
                      <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                        placeholder="--------" className="pl-12 pr-12 bg-neutral-obsidian/50 border border-divine-gold/20 focus:border-divine-gold text-chakra-crown placeholder:text-divine-gold/30"
                        required data-testid="password-input" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-divine-gold/40 hover:text-divine-gold">
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full vedic-button flex items-center justify-center gap-2" data-testid="login-submit-btn">
                    {loading ? <span className="animate-pulse">Entering...</span> : (
                      <>{loginType === "client" ? "Enter Sacred Space" : `Enter ${loginType.charAt(0).toUpperCase() + loginType.slice(1)} Portal`}<ArrowRight className="w-5 h-5" /></>
                    )}
                  </Button>
                </form>
              )}

              {/* OTP Login */}
              {loginMethod === "otp" && (
                <div className="space-y-5">
                  {otpLoginStep === "email" && (
                    <>
                      <div className="space-y-2">
                        <Label className="label-sacred">Email Address</Label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-divine-gold/40" />
                          <Input type="email" value={otpEmail} onChange={(e) => setOtpEmail(e.target.value)} placeholder="your@email.com"
                            className="pl-12 bg-neutral-obsidian/50 border border-divine-gold/20 focus:border-divine-gold text-chakra-crown placeholder:text-divine-gold/30"
                            data-testid="otp-email-input"
                            onKeyDown={(e) => e.key === "Enter" && handleSendLoginOtp()} />
                        </div>
                      </div>
                      <Button onClick={handleSendLoginOtp} disabled={loading} className="w-full vedic-button flex items-center justify-center gap-2" data-testid="send-login-otp-btn">
                        {loading ? "Sending OTP..." : <><Fingerprint className="w-5 h-5" /> Send Login OTP</>}
                      </Button>
                    </>
                  )}

                  {otpLoginStep === "verify" && (
                    <>
                      {otpEmailSent ? (
                        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-center">
                          <p className="text-emerald-400 text-sm font-heading">OTP Sent to Your Email</p>
                          <p className="text-chakra-crown/50 text-xs mt-1">Check your inbox at <span className="text-divine-gold">{otpEmail}</span></p>
                          <p className="text-chakra-crown/40 text-xs mt-1">Valid for 10 minutes</p>
                        </div>
                      ) : (
                        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-center">
                          <p className="text-emerald-400 text-sm font-heading">OTP Sent to Your Email</p>
                          <p className="text-chakra-crown/50 text-xs mt-1">Check your inbox at <span className="text-divine-gold">{otpEmail}</span></p>
                          {otpFallback && <p className="text-amber-400 text-xs mt-2">If you didn't receive the email, check spam or try again.</p>}
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label className="label-sacred">Enter OTP</Label>
                        <Input value={loginOtp} onChange={(e) => setLoginOtp(e.target.value)} placeholder="6-digit OTP"
                          className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown text-center text-xl tracking-[0.2em]"
                          maxLength={6} data-testid="login-otp-input"
                          onKeyDown={(e) => e.key === "Enter" && handleVerifyLoginOtp()} />
                      </div>
                      <Button onClick={handleVerifyLoginOtp} disabled={loading} className="w-full vedic-button flex items-center justify-center gap-2" data-testid="verify-login-otp-btn">
                        {loading ? "Verifying..." : <><ArrowRight className="w-5 h-5" /> Login</>}
                      </Button>
                      <button onClick={() => { setOtpLoginStep("email"); setLoginOtp(""); }}
                        className="text-divine-gold/60 hover:text-divine-gold text-xs w-full text-center">
                        Use a different email
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Forgot Password */}
              {loginMethod === "password" && (
                <div className="mt-4 text-center">
                  <button onClick={() => setShowForgot(true)} className="text-divine-gold/60 hover:text-divine-gold text-sm transition-colors" data-testid="forgot-password-link">
                    Forgot Password?
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default Login;
